import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * TaskBubbleCanvas — the TODO Funnel's smart canvas (Phase 4).
 * docs/TODO-FUNNEL-VISION.md §3.4.
 *
 * Gravity zones (GTD lists), NOT free x/y: the inbox cauldron on top, Next
 * Actions center-stage, Waiting / Projects / Someday around it. Bubbles are
 * sized by priority, tinted by context, dashed while untriaged. Drag a
 * bubble between zones to retriage (optimistic — the parent's onMove may
 * reject to revert, OrgChart pattern). Drop a bubble ONTO another bubble to
 * group them into a project. Tap the check dot to complete (pop + fade).
 * The lens bar derives one-tap filters from the data itself: contexts,
 * agenda people, this-week, quick wins, waiting.
 *
 * Presentational and MUI-free (frontend invariant #4): data via props,
 * colors via CSS variables (+ theme-safe hsla context tints), no fetching.
 * Both TekStak and GAS render this same component.
 */
import React, { useMemo, useState, useCallback } from 'react';
import { DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors, } from '@dnd-kit/core';
import { Paperclip, Trash2, Plus, X, ChevronDown, FilePlus, Undo2, Search, Wand2, Check, CheckCheck, Archive, ArchiveRestore, Download, MessageSquare, Copy, UserPlus, CornerDownRight } from 'lucide-react';
const ENUM_SCORE = { urgent: 90, high: 70, medium: 45, low: 20 };
export function taskScore(t) {
    return t.priority_score ?? ENUM_SCORE[t.priority || 'medium'] ?? 45;
}
export function priorityBand(score) {
    if (score >= 85)
        return 'critical';
    if (score >= 60)
        return 'elevated';
    return 'neutral';
}
/** Slider detents: releasing snaps to the band's canonical score. */
export const BAND_SCORE = { neutral: 45, elevated: 70, critical: 90 };
export function snapScore(score) {
    // Midpoints between detents, so the visual track zones (gray 0-60%,
    // yellow 60-85%, red 85-100%) and the snap regions agree: release the
    // thumb in a zone, land on that zone's detent.
    if (score >= 80)
        return BAND_SCORE.critical;
    if (score >= 58)
        return BAND_SCORE.elevated;
    return BAND_SCORE.neutral;
}
/** @deprecated retained for import compatibility — bands replaced hues. */
export function heatHue(score) {
    return priorityBand(score) === 'critical' ? 4 : priorityBand(score) === 'elevated' ? 45 : 120;
}
const ZONES = [
    { key: 'inbox', label: 'Inbox', hint: 'the cauldron — everything lands here' },
    { key: 'next_actions', label: 'Next Actions', hint: 'single concrete steps you can take now' },
    { key: 'waiting_for', label: 'Waiting For', hint: 'blocked on someone else' },
    { key: 'projects', label: 'Projects', hint: 'multi-step outcomes' },
    { key: 'someday_maybe', label: 'Someday', hint: 'ideas for later' },
    { key: 'reference', label: 'Reference', hint: 'keep, no action' },
];
const CTX_SLUG = {
    '@computer': 'computer',
    '@phone': 'phone',
    '@errands': 'errands',
    '@home': 'home',
    '@work': 'work',
    '@anywhere': 'anywhere',
    '@agenda': 'agenda',
    '@read': 'read',
};
/** Kind chip for the synthesized origin context item (backend, from
 *  source_ref) — labeled by the feed the task came from. */
const ORIGIN_KIND_LABEL = {
    plaud: 'Plaud',
    remarkable: 'reMarkable',
    ia_writer: 'iA Writer',
    import: 'iA Writer',
    google_tasks: 'Google Tasks',
    chat: 'chat',
    mcp: 'chat',
};
/** Quick due-date choices — no typing (Ryan). */
function quickDueOptions() {
    const iso = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };
    const today = new Date();
    const add = (n) => {
        const d = new Date(today);
        d.setDate(d.getDate() + n);
        return d;
    };
    const opts = [
        { label: 'Today', iso: iso(today) },
        { label: 'Tomorrow', iso: iso(add(1)) },
    ];
    const dow = today.getDay(); // 0 Sun … 5 Fri 6 Sat
    const toFri = (5 - dow + 7) % 7;
    if (toFri > 1)
        opts.push({ label: 'Fri', iso: iso(add(toFri)) });
    const toMon = ((1 - dow + 7) % 7) || 7;
    opts.push({ label: `Mon`, iso: iso(add(toMon)) });
    opts.push({ label: 'Next week', iso: iso(add(7)) });
    return opts;
}
const DAY_MS = 86400000;
const STALE_MS = 21 * DAY_MS;
/** Urgency score — decides WHICH tasks survive a zone cap (display order
 *  stays the user's manual position). */
function urgency(t) {
    let s = 0;
    const d = dueMeta(t);
    if (d)
        s += d.overdue ? 500 : d.label === 'today' ? 400 : d.label === 'tomorrow' ? 300 : /^\dd$/.test(d.label) ? 200 : 50;
    s += t.priority === 'urgent' ? 250 : t.priority === 'high' ? 120 : t.priority === 'medium' ? 40 : 0;
    if (t.triage_state === 'needs_review')
        s += 150;
    return s;
}
/** ONE account-grouping implementation for every board (audit §5): canvas
 *  clusters (size-ordered, only when busy) and week columns (alphabetical,
 *  always) both come through here. Tasks with no account form the "General"
 *  group and always lead (Ryan, 2026-08-09 — general work is a first-class
 *  category, not an "uncategorized" afterthought at the bottom). */
export function groupByAccount(shown, { minSize = 0, order = 'alpha' } = {}) {
    if (shown.length < minSize)
        return [{ tasks: shown }];
    const groups = new Map();
    for (const t of shown) {
        const key = t.account_label || '';
        if (!groups.has(key))
            groups.set(key, []);
        groups.get(key).push(t);
    }
    if (minSize > 0 && groups.size < 2)
        return [{ tasks: shown }];
    return [...groups.entries()]
        .sort((a, b) => a[0] === '' ? -1 : b[0] === '' ? 1 : order === 'size' ? b[1].length - a[1].length : a[0].localeCompare(b[0]))
        .map(([label, tasks]) => ({ label, tasks }));
}
/** Canvas clustering: only once a zone is busy (≥8 visible), by size. */
function clusterAccounts(shown, on) {
    if (!on)
        return [{ tasks: shown }];
    return groupByAccount(shown, { minSize: 8, order: 'size' });
}
/** Stale: untouched 3+ weeks, nothing due, not high/urgent — fades on the
 *  canvas and collects under the Stale lens for bulk cleanup. */
function isStale(t) {
    if (!t.updated_at)
        return false;
    if (t.priority === 'urgent' || t.priority === 'high')
        return false;
    if (dueMeta(t))
        return false;
    return Date.now() - new Date(t.updated_at).getTime() > STALE_MS;
}
function dueMeta(task) {
    if (!task.due_date)
        return null;
    // Date-only strings must parse as LOCAL dates — new Date('2026-08-07') is
    // UTC midnight, i.e. the previous evening in Pacific (off-by-one bug).
    const raw = String(task.due_date);
    const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T00:00:00(?:\.0+)?Z?$)/);
    const due = ymd
        ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
        : new Date(raw);
    if (isNaN(due.getTime()))
        return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const diff = Math.round((dueDay.getTime() - today.getTime()) / DAY_MS);
    if (diff < 0)
        return { label: `${Math.abs(diff)}d over`, overdue: true };
    if (diff === 0)
        return { label: 'today', overdue: false };
    if (diff === 1)
        return { label: 'tomorrow', overdue: false };
    if (diff < 7)
        return { label: `${diff}d`, overdue: false };
    return { label: due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }), overdue: false };
}
function agendaPerson(task) {
    for (const t of task.tags || []) {
        if (t.startsWith('agenda:'))
            return t.slice(7);
    }
    return null;
}
function involvedPerson(task) {
    for (const t of task.tags || []) {
        if (t.startsWith('person:'))
            return t.slice(7);
    }
    return null;
}
/** Every way a task can relate to a person (GTD: waiting-for, @agenda,
 *  or simply involved in MY next action). */
function taskPeople(task) {
    return [task.waiting_for_who, agendaPerson(task), involvedPerson(task)].filter(Boolean);
}
function deriveLenses(tasks) {
    // Focus: the canvas curates itself — what deserves attention NOW.
    const focusTest = (t) => {
        if (t.status === 'completed')
            return false;
        if (t.triage_state === 'needs_review')
            return true;
        // Untriaged = nothing has looked at it yet (incl. a card you JUST
        // created from a zone footer) — always deserves attention.
        if (t.triage_state === 'untriaged')
            return true;
        if (t.gtd_list === 'inbox')
            return true;
        if (t.priority === 'urgent')
            return true;
        if (t.priority === 'high' && t.gtd_list === 'next_actions')
            return true;
        const d = dueMeta(t);
        if (d && (d.overdue || d.label === 'today' || d.label === 'tomorrow' || /^\dd$/.test(d.label)))
            return true;
        return false;
    };
    const lenses = [
        { key: 'focus', label: 'Focus', test: focusTest },
        { key: 'all', label: 'All', test: () => true },
    ];
    lenses.push({
        key: 'week',
        label: 'This week',
        test: (t) => {
            const d = dueMeta(t);
            return !!d && (d.overdue || d.label === 'today' || d.label === 'tomorrow' || /^\dd$/.test(d.label));
        },
    });
    if (tasks.some((t) => (t.tags || []).includes('quick-win'))) {
        lenses.push({ key: 'quick', label: '2-min wins', test: (t) => (t.tags || []).includes('quick-win') });
    }
    lenses.push({ key: 'waiting', label: 'Waiting', test: (t) => t.gtd_list === 'waiting_for' && t.status !== 'completed' });
    // ONE review surface (audit §5): the lens covers everything AI touched
    // that a human hasn't confirmed — accept/keep lives in the focus editor.
    const reviewTest = (t) => (t.triage_state === 'needs_review' || t.triage_state === 'auto_triaged') && t.status !== 'completed';
    if (tasks.some(reviewTest)) {
        lenses.push({ key: 'review', label: 'Review', test: reviewTest });
    }
    if (tasks.some((t) => t.status === 'completed')) {
        lenses.push({ key: 'done', label: 'Done', test: (t) => t.status === 'completed' });
    }
    if (tasks.some(isStale)) {
        lenses.push({ key: 'stale', label: 'Stale', test: isStale });
    }
    // Archived lens is ALWAYS offered (Timeframe group) — selecting it is how
    // hosts know to fetch the shelf; the count fills in once rows arrive.
    lenses.push({ key: 'archived', label: 'Archived', test: (t) => !!t.archived_at });
    const contexts = new Set();
    const people = new Set();
    for (const t of tasks) {
        if (t.context && t.context !== '@agenda')
            contexts.add(t.context);
        for (const p of taskPeople(t))
            people.add(p);
    }
    for (const c of Array.from(contexts).sort()) {
        lenses.push({ key: `ctx:${c}`, label: c, test: (t) => t.context === c });
    }
    // One lens per person = EVERYTHING about them: delegated to them,
    // agenda for your next meeting, and your own actions involving them.
    for (const p of Array.from(people).sort()) {
        lenses.push({ key: `who:${p}`, label: p, test: (t) => taskPeople(t).includes(p) });
    }
    // Archiving invariant (vision doc §3.9): archived rows appear ONLY under
    // the Archived lens — every other lens excludes them by construction.
    return lenses.map((l) => l.key === 'archived' ? l : { ...l, test: (t) => !t.archived_at && l.test(t) });
}
export const DEFAULT_TASK_FILTER = {
    lensKey: 'focus',
    query: '',
    acctFilter: null,
    clusterOn: true,
};
/** The page-level filter: same semantics as the canvas's internal one, so
 *  every view (canvas / week / matrix) shows the same task set. */
export function applyTaskFilter(tasks, filter) {
    const lenses = deriveLenses(tasks);
    const lens = lenses.find((l) => l.key === filter.lensKey) || lenses[0];
    const q = (filter.query || '').trim().toLowerCase();
    return tasks
        .filter((t) => !filter.acctFilter || (t.account_label || '').toUpperCase() === filter.acctFilter.toUpperCase())
        .filter((t) => lens.test(t))
        .filter((t) => !q ||
        t.title.toLowerCase().includes(q) ||
        (t.account_label || '').toLowerCase().includes(q) ||
        (t.waiting_for_who || '').toLowerCase().includes(q) ||
        (t.tags || []).some((tag) => tag.toLowerCase().includes(q)));
}
function taskLine(t) {
    const bits = [
        t.account_label ? t.account_label : null,
        t.due_date ? `due ${String(t.due_date).slice(5, 10).replace('-', '/')}` : null,
        t.waiting_for_who ? `waiting on ${t.waiting_for_who}` : null,
    ].filter(Boolean);
    return `• ${t.title}${bits.length ? ` (${bits.join(' · ')})` : ''}`;
}
/** Slack-ready plain text — paste straight into a channel or DM. */
export function tasksToSlackText(tasks) {
    return tasks.map(taskLine).join('\n');
}
/** Email-ready: HTML table + plain-text fallback on the clipboard. */
export async function copyTasksForEmail(tasks) {
    const esc = (v) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const rows = tasks
        .map((t) => {
        const cells = [
            t.due_date ? String(t.due_date).slice(0, 10) : '',
            t.title,
            t.account_label || '',
            t.waiting_for_who || '',
        ];
        return `<tr>${cells.map((c) => `<td style="padding:4px 10px;border:1px solid #d1d5db;">${esc(c)}</td>`).join('')}</tr>`;
    })
        .join('');
    const html = `<table style="border-collapse:collapse;font-family:sans-serif;font-size:13px;"><tr>${['Date', 'Task', 'Account', 'Assignee']
        .map((h) => `<th style="padding:4px 10px;border:1px solid #d1d5db;text-align:left;background:#f3f4f6;">${h}</th>`)
        .join('')}</tr>${rows}</table>`;
    const text = tasksToSlackText(tasks);
    try {
        if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([html], { type: 'text/html' }),
                    'text/plain': new Blob([text], { type: 'text/plain' }),
                }),
            ]);
            return true;
        }
    }
    catch (e) { /* fall through */ }
    try {
        await navigator.clipboard.writeText(text);
        return true;
    }
    catch (e) {
        return false;
    }
}
/** Copy selected tasks for Slack, report the event. */
export async function copyTasksForSlack(tasks) {
    try {
        await navigator.clipboard.writeText(tasksToSlackText(tasks));
        return true;
    }
    catch (e) {
        return false;
    }
}
export function exportEvents(tasks, action, detail) {
    return tasks.map((t) => ({ task_id: t.id, action, detail: { count: tasks.length, ...detail } }));
}
/** CSV download shared by every bulk bar (the matrix toolbar keeps its
 *  richer detail column; this is the selection-sized export). */
export function downloadTasksCsv(tasks) {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [
        ['Date', 'Task', 'Account', 'Assignee', 'List', 'Priority', 'Tags'].map(esc).join(','),
        ...tasks.map((t) => [
            t.due_date ? String(t.due_date).slice(0, 10) : '',
            t.title,
            t.account_label || '',
            t.waiting_for_who || '',
            t.gtd_list,
            t.priority || 'medium',
            (t.tags || []).join(' '),
        ].map(esc).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `tasks-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
}
export function TaskBulkBar({ tasks, people, moveOptions, moveLabel = 'Move to list', onMove, onComplete, onArchive, onBulkArchive, onDelete, onAssign, onLogEvents, onClear, }) {
    const [armed, setArmed] = useState(false);
    const [menu, setMenu] = useState(null);
    const [sharePerson, setSharePerson] = useState(null);
    const ids = tasks.map((t) => t.id);
    if (!ids.length)
        return null;
    const closeMenus = () => { setMenu(null); setSharePerson(null); };
    const run = (fn) => {
        closeMenus();
        onClear();
        void (async () => {
            for (const id of ids) {
                try {
                    await fn(id);
                }
                catch (e) { /* one failure never stops the batch */ }
            }
        })();
    };
    return (_jsxs("div", { className: "tbc-bulk", role: "toolbar", "aria-label": "Bulk actions", children: [_jsxs("span", { className: "tbc-bulk-count", children: [ids.length, " selected"] }), onComplete && (_jsx("button", { type: "button", className: "tbc-bulk-btn", title: "Complete all", "aria-label": "Complete selected", onClick: () => run((id) => onComplete(id)), children: _jsx(Check, { size: 13 }) })), onMove && moveOptions && moveOptions.length > 0 && (_jsxs("span", { className: "tbc-bulk-anchor", children: [_jsx("button", { type: "button", className: `tbc-bulk-btn ${menu === 'move' ? 'tbc-bulk-btn-open' : ''}`, title: moveLabel, "aria-label": moveLabel, "aria-expanded": menu === 'move', onClick: () => setMenu(menu === 'move' ? null : 'move'), children: _jsx(CornerDownRight, { size: 13 }) }), menu === 'move' && (_jsxs(_Fragment, { children: [_jsx("span", { className: "tbc-lens-veil", onMouseDown: closeMenus }), _jsx("span", { className: "tbc-bulk-menu", role: "menu", children: moveOptions.map((o) => (_jsx("button", { type: "button", onClick: () => run((id) => onMove(id, o.key)), children: o.label }, o.key))) })] }))] })), _jsx("button", { type: "button", className: "tbc-bulk-btn", title: "Copy as Slack-ready text", "aria-label": "Copy for Slack", onClick: () => {
                    void copyTasksForSlack(tasks).then((ok) => {
                        if (ok)
                            onLogEvents?.(exportEvents(tasks, 'exported_slack', { target: 'clipboard' }));
                    });
                    onClear();
                }, children: _jsx(MessageSquare, { size: 13 }) }), _jsx("button", { type: "button", className: "tbc-bulk-btn", title: "Copy as an email-ready table", "aria-label": "Copy for email", onClick: () => {
                    void copyTasksForEmail(tasks).then((ok) => {
                        if (ok)
                            onLogEvents?.(exportEvents(tasks, 'exported_email', { target: 'clipboard' }));
                    });
                    onClear();
                }, children: _jsx(Copy, { size: 13 }) }), _jsx("button", { type: "button", className: "tbc-bulk-btn", title: "Download CSV", "aria-label": "Download CSV", onClick: () => {
                    downloadTasksCsv(tasks);
                    onLogEvents?.(exportEvents(tasks, 'exported_csv', {}));
                    onClear();
                }, children: _jsx(Download, { size: 13 }) }), onAssign && (people || []).length > 0 && (_jsxs("span", { className: "tbc-bulk-anchor", children: [_jsx("button", { type: "button", className: `tbc-bulk-btn ${menu === 'share' ? 'tbc-bulk-btn-open' : ''}`, title: "Share \u2014 assign everyone selected to a person", "aria-label": "Share with a person", "aria-expanded": menu === 'share', onClick: () => setMenu(menu === 'share' ? null : 'share'), children: _jsx(UserPlus, { size: 13 }) }), menu === 'share' && (_jsxs(_Fragment, { children: [_jsx("span", { className: "tbc-lens-veil", onMouseDown: closeMenus }), _jsxs("span", { className: "tbc-bulk-menu", role: "menu", children: [!sharePerson && (people || []).slice(0, 30).map((p) => (_jsxs("button", { type: "button", onClick: () => setSharePerson(p), children: [p.name, p.hint ? _jsx("span", { className: "tbc-bulk-menu-hint", children: p.hint }) : null] }, `${p.name}:${p.email || ''}`))), sharePerson && (_jsxs(_Fragment, { children: [_jsxs("span", { className: "tbc-bulk-menu-title", children: [sharePerson.name, " \u2014 as what?"] }), [
                                                ['waiting', 'Waiting on them'],
                                                ['agenda', 'Agenda (raise with them)'],
                                                ['involves', 'Involves them'],
                                            ].map(([relation, label]) => (_jsx("button", { type: "button", onClick: () => {
                                                    const sel = tasks;
                                                    run((id) => onAssign(id, { name: sharePerson.name, email: sharePerson.email, relation }));
                                                    onLogEvents?.(exportEvents(sel, 'shared', { person: sharePerson.name, relation }));
                                                }, children: label }, relation)))] }))] })] }))] })), (onBulkArchive || onArchive) && (_jsx("button", { type: "button", className: "tbc-bulk-btn", title: "Archive \u2014 shelve, recover any time under the Archived lens", "aria-label": "Archive selected", onClick: () => {
                    if (onBulkArchive) {
                        // ONE batched request; the handler is optimistic (cards are
                        // already gone) and reverts them itself on rejection.
                        const batch = ids;
                        closeMenus();
                        onClear();
                        void onBulkArchive(batch);
                    }
                    else {
                        run((id) => onArchive(id));
                    }
                }, children: _jsx(Archive, { size: 13 }) })), onDelete && (_jsx("button", { type: "button", className: `tbc-bulk-btn tbc-bulk-delete ${armed ? 'tbc-bulk-armed' : ''}`, title: armed ? 'Click again to delete all' : 'Delete', "aria-label": armed ? 'Confirm delete selected' : 'Delete selected', onClick: () => {
                    if (!armed) {
                        setArmed(true);
                        window.setTimeout(() => setArmed(false), 3000);
                    }
                    else {
                        run((id) => onDelete(id));
                    }
                }, children: _jsx(Trash2, { size: 13 }) })), _jsx("button", { type: "button", className: "tbc-bulk-btn tbc-bulk-clear", title: "Clear selection", "aria-label": "Clear selection", onClick: onClear, children: _jsx(X, { size: 13 }) })] }));
}
// ── TaskQuickAdd: the in-place "+" → input creator ───────────────────────
// Used by canvas zone footers (creates into that gtd_list) and week
// columns (creates with that day's due date). Capture-is-sacred: one
// field, Enter saves, input clears for the next thought, Esc closes.
export function TaskQuickAdd({ placeholder = 'New task…', title = 'New task', onSubmit, }) {
    const [open, setOpen] = useState(false);
    const [val, setVal] = useState('');
    const submit = () => {
        const v = val.trim();
        if (!v)
            return;
        void Promise.resolve(onSubmit(v));
        setVal('');
    };
    if (!open) {
        return (_jsx("button", { type: "button", className: "tbc-qadd-btn", title: title, "aria-label": title, onClick: (e) => { e.stopPropagation(); setOpen(true); }, onPointerDown: (e) => e.stopPropagation(), children: _jsx(Plus, { size: 12 }) }));
    }
    return (_jsx("span", { className: "tbc-qadd", onPointerDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), children: _jsx("input", { className: "tbc-qadd-input", placeholder: placeholder, value: val, autoFocus: true, onChange: (e) => setVal(e.target.value), onKeyDown: (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                }
                if (e.key === 'Escape') {
                    e.preventDefault();
                    setVal('');
                    setOpen(false);
                }
            }, onBlur: () => { if (!val.trim())
                setOpen(false); }, "aria-label": title }) }));
}
export function Bubble({ task, childTasks, dense, dragDisabled, compact, isSelected, onToggleSelect, insertSide, dragging, overlay, completing, groupTarget, onCheck, onReopen, onSelect, initialMode, onUpdate, onAttach, onAttachFile, onGetContextUrl, onLoadContext, onRemoveContext, onDelete, onArchive, onUnarchive, accounts, people, onAssign, onCreatePerson, renderBadge, }) {
    // Inline editing happens ON the bubble — no modal. mode: which inline
    // input is open; dragging is disabled while one is.
    const [mode, setMode] = useState(null);
    const [draft, setDraft] = useState('');
    const [clips, setClips] = useState(null);
    const [openClip, setOpenClip] = useState(null);
    const [showSteps, setShowSteps] = useState(false);
    const [newPerson, setNewPerson] = useState(null);
    const [pendingWho, setPendingWho] = useState(null);
    const [heatDraft, setHeatDraft] = useState(null);
    const score = heatDraft ?? taskScore(task);
    const band = priorityBand(heatDraft != null ? snapScore(heatDraft) : score);
    const commitHeat = () => {
        if (heatDraft == null)
            return;
        const snapped = snapScore(heatDraft);
        if (snapped !== taskScore(task))
            onUpdate?.(task.id, { priority_score: snapped });
        setHeatDraft(null);
    };
    const commitWho = (relation) => {
        if (!pendingWho)
            return;
        if (onAssign)
            onAssign(task.id, { ...pendingWho, relation });
        else if (relation === 'waiting')
            onUpdate?.(task.id, { waiting_for_who: pendingWho.name, gtd_list: 'waiting_for' });
        else if (relation === 'agenda')
            onUpdate?.(task.id, { context: '@agenda', tags: [...(task.tags || []), `agenda:${pendingWho.name}`] });
        else
            onUpdate?.(task.id, { tags: [...(task.tags || []), `person:${pendingWho.name}`] });
        setPendingWho(null);
        setMode(null);
    };
    const createAndAssign = () => {
        if (!newPerson || !onCreatePerson)
            return;
        const payload = {
            name: newPerson.name,
            email: newPerson.email.trim() || undefined,
            phone: newPerson.phone.trim() || undefined,
            account: task.account_label || null,
        };
        Promise.resolve(onCreatePerson(payload))
            .then((p) => setPendingWho({ name: p.name, email: p.email }))
            .catch(() => { });
        setNewPerson(null);
    };
    const [fileOver, setFileOver] = useState(false);
    const MAX_FILE = 5 * 1024 * 1024;
    const sendFile = (f) => {
        if (!onAttachFile || f.size > MAX_FILE)
            return;
        const reader = new FileReader();
        reader.onload = () => {
            const b64 = String(reader.result || '').split(',')[1] || '';
            if (b64)
                onAttachFile(task.id, { name: f.name, mime: f.type || 'application/octet-stream', size: f.size, data_base64: b64 });
        };
        reader.readAsDataURL(f);
    };
    const toggleClips = () => {
        if (compact) {
            onSelect?.(task, 'context', anchorRect());
            return;
        }
        if (clips) {
            setClips(null);
            return;
        }
        if (!onLoadContext)
            return;
        setClips('loading');
        Promise.resolve(onLoadContext(task.id))
            .then((items) => setClips(Array.isArray(items) ? items : []))
            .catch(() => setClips([]));
    };
    const clipLabel = (c) => c.caption ||
        String(c.pointer?.url || c.pointer?.link || c.pointer?.text || c.pointer?.name || c.pointer?.note_id || c.kind).slice(0, 60);
    const clipFull = (c) => [c.caption, c.pointer?.text, c.pointer?.url, c.pointer?.link, c.pointer?.excerpt, c.pointer?.name]
        .map((v) => (v == null ? '' : String(v)))
        .filter((v, i, arr) => v && arr.indexOf(v) === i);
    const openStoredFile = (c) => {
        if (!onGetContextUrl)
            return;
        Promise.resolve(onGetContextUrl(task.id, c.id)).then((u) => {
            if (u)
                window.open(u, '_blank', 'noopener');
        });
    };
    const clipHref = (c) => {
        const h = c.pointer?.url || c.pointer?.link;
        return typeof h === 'string' && /^https?:\/\//i.test(h) ? h : null;
    };
    const done = task.status === 'completed';
    const drag = useDraggable({ id: `bubble:${task.id}`, disabled: overlay || dragDisabled || mode !== null || done });
    const drop = useDroppable({ id: `onto:${task.id}`, disabled: overlay || dragging });
    const ctxSlug = task.context ? CTX_SLUG[task.context] || 'anywhere' : 'none';
    const due = dueMeta(task);
    const person = task.waiting_for_who || agendaPerson(task) || involvedPerson(task);
    const personKind = task.waiting_for_who ? 'waiting' : agendaPerson(task) ? 'agenda' : involvedPerson(task) ? 'involves' : null;
    // Border vocabulary (audit §5): dash = "not yet triaged" ONLY; AI results
    // awaiting confirmation read through the Review lens + focus-editor row.
    const untriaged = task.triage_state === 'untriaged';
    const tags = (task.tags || []).filter((t) => !t.startsWith('agenda:'));
    // Pill budget (redesign 2026-08-07): a compact tile shows at most THREE
    // info pills — the rest collapse into "+N", which opens the focus editor.
    // Priority order: due (time pressure) > person > account > the rest.
    const PILL_CAP = 3;
    const pillOrder = [];
    if (compact) {
        if (dueMeta(task))
            pillOrder.push('due');
        if (person)
            pillOrder.push('person');
        if (task.account_label)
            pillOrder.push('acct');
        if (task.context)
            pillOrder.push('ctx');
        if ((childTasks?.length || 0) > 0)
            pillOrder.push('steps');
        if ((task.context_count || 0) > 0)
            pillOrder.push('clips');
        for (const t of tags)
            pillOrder.push(`tag:${t}`);
    }
    const showPill = (key) => !compact || pillOrder.indexOf(key) < PILL_CAP;
    const pillOverflow = compact ? Math.max(0, pillOrder.length - PILL_CAP) : 0;
    const stop = (e) => e.stopPropagation();
    const open = (m, seed = '') => {
        if (compact) {
            // No in-place growth in the flow — the focus editor grows from
            // this tile with the clicked affordance already active.
            onSelect?.(task, m, anchorRect());
            return;
        }
        setDraft(seed);
        setMode(m);
    };
    // Focus-editor hand-off: arrive with the clicked affordance open — and
    // ALWAYS load context in the full editor (Ryan, 2026-08-09): provenance is
    // the point of a connected task, so the origin note / attachments show
    // without hunting for the 📎 pill. Origin arrives pre-expanded.
    React.useEffect(() => {
        if (compact)
            return;
        if (onLoadContext) {
            setClips('loading');
            Promise.resolve(onLoadContext(task.id))
                .then((items) => {
                const arr = Array.isArray(items) ? items : [];
                // Auto-load shows content or nothing — never a bare "No
                // attachments" row on every plain task.
                setClips(arr.length ? arr : null);
                if (arr.some((c) => c.kind === 'origin'))
                    setOpenClip('origin');
            })
                .catch(() => setClips(null));
        }
        if (!initialMode || initialMode === 'context')
            return;
        const seeds = {
            title: task.title,
            who: task.waiting_for_who || '',
            acct: task.account_label || '',
            due: task.due_date ? String(task.due_date).slice(0, 10) : '',
            'due-pick': task.due_date ? String(task.due_date).slice(0, 10) : '',
        };
        setDraft(seeds[initialMode] ?? '');
        setMode(initialMode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const commit = () => {
        const v = draft.trim();
        if (mode === 'title' && v && v !== task.title)
            onUpdate?.(task.id, { title: v });
        if (mode === 'tag' && v)
            onUpdate?.(task.id, { tags: [...(task.tags || []), v.replace(/^#/, '')] });
        if (mode === 'attach' && v) {
            onAttach?.(task.id, /^https?:\/\//i.test(v) ? { kind: 'url', pointer: { url: v } } : { kind: 'free_text', pointer: { text: v } });
        }
        if (mode === 'due' || mode === 'due-pick') {
            const prev = task.due_date ? String(task.due_date).slice(0, 10) : '';
            if (v !== prev)
                onUpdate?.(task.id, { due_date: v || null });
        }
        if (mode === 'acct' && v && v.toUpperCase() !== (task.account_label || '').toUpperCase()) {
            onUpdate?.(task.id, { account: v.toUpperCase() });
        }
        if (mode === 'who' && v && v !== (task.waiting_for_who || '')) {
            onUpdate?.(task.id, { waiting_for_who: v, gtd_list: 'waiting_for' });
        }
        setMode(null);
    };
    const inlineKeys = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commit();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            setMode(null);
        }
    };
    const classes = [
        'tbc-bubble',
        `tbc-size-${task.priority || 'medium'}`,
        `tbc-ctx-${ctxSlug}`,
        untriaged ? 'tbc-untriaged' : '',
        task.is_project ? 'tbc-project' : '',
        dragging ? 'tbc-dragging' : '',
        overlay ? 'tbc-overlay' : '',
        completing ? 'tbc-completing' : '',
        groupTarget ? 'tbc-group-target' : '',
        mode !== null ? 'tbc-editing' : '',
        compact ? 'tbc-compact' : '',
        done ? 'tbc-done' : '',
        `tbc-band-${band}`,
        clips || showSteps ? 'tbc-expanded' : '',
        fileOver ? 'tbc-file-over' : '',
        dense ? 'tbc-still' : '',
        isStale(task) ? 'tbc-stale' : '',
        isSelected ? 'tbc-selected' : '',
        task.today && !done ? 'tbc-today' : '',
        task.archived_at ? 'tbc-archived' : '',
        insertSide ? `tbc-insert-${insertSide}` : '',
    ].filter(Boolean).join(' ');
    const rootRef = React.useRef(null);
    const setRefs = (el) => {
        rootRef.current = el;
        drag.setNodeRef(el);
        drop.setNodeRef(el);
    };
    const anchorRect = () => rootRef.current?.getBoundingClientRect();
    return (_jsxs("div", { ref: setRefs, className: classes, style: overlay || dense ? undefined : { viewTransitionName: `tbc-${task.id.replace(/[^a-zA-Z0-9-]/g, '')}` }, ...drag.attributes, ...drag.listeners, onClick: (e) => {
            if ((e.shiftKey || e.metaKey) && onToggleSelect) {
                e.stopPropagation();
                onToggleSelect(task.id);
                return;
            }
            onSelect?.(task, undefined, anchorRect());
        }, onDragOver: onAttachFile ? (e) => { if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            e.stopPropagation();
            setFileOver(true);
        } } : undefined, onDragLeave: onAttachFile ? () => setFileOver(false) : undefined, onDrop: onAttachFile ? (e) => {
            if (!e.dataTransfer.files?.length)
                return;
            e.preventDefault();
            e.stopPropagation();
            setFileOver(false);
            sendFile(e.dataTransfer.files[0]);
        } : undefined, children: [!overlay && onToggleSelect && (_jsx("button", { type: "button", className: `tbc-sel ${isSelected ? 'tbc-sel-on' : ''}`, role: "checkbox", "aria-checked": !!isSelected, "aria-label": isSelected ? 'Deselect task' : 'Select task', title: isSelected ? 'Deselect' : 'Select for bulk actions', onClick: (e) => { stop(e); onToggleSelect(task.id); }, onPointerDown: stop })), _jsx("button", { type: "button", className: `tbc-check ${done ? 'tbc-check-done' : ''}`, "aria-label": done ? 'Reopen task' : 'Complete task', title: done ? 'Done — click to reopen' : 'Complete', onClick: (e) => {
                    e.stopPropagation();
                    if (done)
                        onReopen?.(task.id);
                    else
                        onCheck?.(task.id);
                }, onPointerDown: stop, children: _jsx(Check, { size: 13, strokeWidth: 3, "aria-hidden": true }) }), !overlay && mode === null && (task.archived_at ? onUnarchive : onArchive) && (_jsx("span", { className: "tbc-acts", onPointerDown: stop, children: task.archived_at ? (_jsx("button", { type: "button", className: "tbc-act", title: "Unarchive \u2014 back to the boards exactly as it was", "aria-label": "Unarchive task", onClick: (e) => { stop(e); onUnarchive?.(task.id); }, children: _jsx(ArchiveRestore, { size: 11 }) })) : (_jsx("button", { type: "button", className: "tbc-act", title: "Archive \u2014 shelve, recover any time under the Archived lens", "aria-label": "Archive task", onClick: (e) => { stop(e); onArchive?.(task.id); }, children: _jsx(Archive, { size: 11 }) })) })), mode === 'title' ? (_jsx("textarea", { className: "tbc-title-input", value: draft, rows: 2, autoFocus: true, onChange: (e) => setDraft(e.target.value), onKeyDown: inlineKeys, onBlur: commit, onPointerDown: stop, onClick: stop })) : (_jsx("span", { className: "tbc-title", title: onUpdate ? 'Click to edit' : undefined, onClick: (e) => {
                    if ((e.shiftKey || e.metaKey) && onToggleSelect) {
                        stop(e);
                        onToggleSelect(task.id);
                        return;
                    }
                    if (onUpdate && !overlay) {
                        stop(e);
                        open('title', task.title);
                    }
                }, children: task.title })), mode === 'attach' && (_jsxs("span", { className: "tbc-attach-row", onPointerDown: stop, children: [_jsx("input", { className: "tbc-inline-input", placeholder: "Paste a link or Drive URL, or type a note\u2026", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onKeyDown: inlineKeys, onClick: stop }), onAttachFile && (_jsxs("label", { className: "tbc-file-btn", title: "Attach a file (or drag one onto the bubble)", onClick: stop, children: [_jsx(FilePlus, { size: 12 }), _jsx("input", { type: "file", className: "tbc-file-input", onChange: (e) => {
                                    const f = e.target.files?.[0];
                                    if (f)
                                        sendFile(f);
                                    setMode(null);
                                } })] })), _jsx("button", { type: "button", className: "tbc-ctx-chip", onClick: (e) => { stop(e); commit(); }, children: "Add" })] })), _jsxs("span", { className: "tbc-meta", onPointerDown: mode === 'tag' ? stop : undefined, children: [renderBadge?.(task), task.is_mine === false && (_jsxs("span", { className: "tbc-pill tbc-pill-from", title: `${task.owner_name || 'A teammate'} is waiting on you for this`, children: ["from ", task.owner_name || 'teammate'] })), mode === 'who' ? (_jsx("input", { className: "tbc-inline-input tbc-who-input", placeholder: "Who?", value: draft, autoFocus: true, onFocus: (e) => e.currentTarget.select(), onChange: (e) => setDraft(e.target.value), onKeyDown: inlineKeys, onBlur: () => window.setTimeout(() => setMode((m) => (m === 'who' ? null : m)), 150), onClick: stop, onPointerDown: stop })) : person && showPill('person') ? (_jsx("button", { type: "button", className: `tbc-pill tbc-pill-person tbc-pill-person-${personKind}`, title: personKind === 'waiting' ? 'Waiting on — click to change' : personKind === 'agenda' ? 'Agenda item for your next meeting — click to change' : 'Involves — click to change', onClick: (e) => { if (onUpdate || onAssign) {
                            stop(e);
                            open('who', task.waiting_for_who || '');
                        } }, onPointerDown: stop, children: person })) : null, mode === 'acct' ? (_jsx("input", { className: "tbc-inline-input tbc-acct-input", placeholder: "FTB\u2026", value: draft, autoFocus: true, onFocus: (e) => e.currentTarget.select(), onChange: (e) => setDraft(e.target.value), onKeyDown: inlineKeys, onBlur: () => window.setTimeout(() => setMode((m) => (m === 'acct' ? null : m)), 150), onClick: stop, onPointerDown: stop })) : task.account_label && showPill('acct') ? (_jsx("button", { type: "button", className: "tbc-pill tbc-pill-acct", title: "Account \u2014 click to change", onClick: (e) => { if (onUpdate) {
                            stop(e);
                            open('acct', task.account_label || '');
                        } }, onPointerDown: stop, children: task.account_label })) : null, task.context && mode !== 'context' && showPill('ctx') && (_jsx("button", { type: "button", className: "tbc-pill tbc-pill-ctx", title: "Context \u2014 click to change", onClick: (e) => { if (onUpdate) {
                            stop(e);
                            open('context');
                        } }, onPointerDown: stop, children: task.context })), mode === 'due-pick' ? (_jsx("input", { type: "date", className: "tbc-inline-input tbc-due-input", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onKeyDown: inlineKeys, onBlur: commit, onClick: stop, onPointerDown: stop })) : mode !== 'due' && due && showPill('due') ? (_jsx("button", { type: "button", className: `tbc-pill ${due.overdue ? 'tbc-pill-overdue' : 'tbc-pill-due'}`, title: "Due date \u2014 click to change", onClick: (e) => {
                            if (onUpdate) {
                                stop(e);
                                open('due', task.due_date ? String(task.due_date).slice(0, 10) : '');
                            }
                        }, onPointerDown: stop, children: due.label })) : null, (childTasks?.length || 0) > 0 && showPill('steps') && (_jsxs("button", { type: "button", className: `tbc-pill tbc-pill-steps ${showSteps ? 'tbc-pill-clip-open' : ''}`, title: showSteps ? 'Hide steps' : `Show ${childTasks.length} step(s)`, onClick: (e) => { stop(e); setShowSteps(!showSteps); }, onPointerDown: stop, children: [childTasks.length, " steps"] })), (task.context_count || 0) > 0 && showPill('clips') && (_jsxs("button", { type: "button", className: `tbc-pill tbc-pill-clip ${clips ? 'tbc-pill-clip-open' : ''}`, title: clips ? 'Hide attachments' : `Show ${task.context_count} attachment(s)`, onClick: (e) => { stop(e); toggleClips(); }, onPointerDown: stop, children: [_jsx(Paperclip, { size: 9 }), task.context_count] })), tags.filter((t) => showPill(`tag:${t}`)).map((t) => (_jsxs("span", { className: "tbc-pill tbc-pill-tag", children: [t, onUpdate && (_jsx("button", { type: "button", className: "tbc-tag-x", "aria-label": `Remove tag ${t}`, onClick: (e) => {
                                    stop(e);
                                    onUpdate(task.id, { tags: (task.tags || []).filter((x) => x !== t) });
                                }, onPointerDown: stop, children: _jsx(X, { size: 8 }) }))] }, t))), pillOverflow > 0 && (_jsxs("button", { type: "button", className: "tbc-pill tbc-pill-more", title: `${pillOverflow} more — open the editor`, "aria-label": `${pillOverflow} more details`, onClick: (e) => { stop(e); onSelect?.(task, undefined, anchorRect()); }, onPointerDown: stop, children: ["+", pillOverflow] })), onUpdate && mode === 'tag' && (_jsx("input", { className: "tbc-inline-input tbc-tag-input", placeholder: "tag", value: draft, autoFocus: true, onChange: (e) => setDraft(e.target.value), onKeyDown: inlineKeys, onBlur: commit, onClick: stop })), onUpdate && mode === null && !overlay && compact && (_jsx("button", { type: "button", className: "tbc-pill tbc-tag-add", title: "Add details \u2014 person, account, due, tags", "aria-label": "Add details", onClick: (e) => { stop(e); onSelect?.(task, undefined, anchorRect()); }, onPointerDown: stop, children: _jsx(Plus, { size: 8 }) })), onUpdate && mode === null && !overlay && !compact && (_jsxs(_Fragment, { children: [!person && (_jsxs("button", { type: "button", className: "tbc-pill tbc-tag-add", title: "Assign / waiting on a person", "aria-label": "Assign person", onClick: (e) => { stop(e); open('who'); }, onPointerDown: stop, children: [_jsx(Plus, { size: 8 }), "who"] })), !task.account_label && (_jsxs("button", { type: "button", className: "tbc-pill tbc-tag-add", title: "Link to an account (FTB, EDD, \u2026)", "aria-label": "Set account", onClick: (e) => { stop(e); open('acct'); }, onPointerDown: stop, children: [_jsx(Plus, { size: 8 }), "acct"] })), !task.context && (_jsxs("button", { type: "button", className: "tbc-pill tbc-tag-add", title: "Set context (@computer, @phone, \u2026)", "aria-label": "Set context", onClick: (e) => { stop(e); open('context'); }, onPointerDown: stop, children: [_jsx(Plus, { size: 8 }), "@"] })), !due && (_jsxs("button", { type: "button", className: "tbc-pill tbc-tag-add", title: "Set due date", "aria-label": "Set due date", onClick: (e) => { stop(e); open('due'); }, onPointerDown: stop, children: [_jsx(Plus, { size: 8 }), "due"] })), _jsxs("button", { type: "button", className: "tbc-pill tbc-tag-add", title: "Add tag", "aria-label": "Add tag", onClick: (e) => { stop(e); open('tag'); }, onPointerDown: stop, children: [_jsx(Plus, { size: 8 }), "tag"] })] }))] }), onUpdate && !overlay && (_jsx("input", { type: "range", className: "tbc-heat", min: 0, max: 100, value: score, "aria-label": "Importance", title: `Importance ${score}/100 — slide to adjust`, onChange: (e) => setHeatDraft(Number(e.target.value)), onPointerDown: (e) => e.stopPropagation(), onPointerUp: commitHeat, onKeyUp: commitHeat, onBlur: commitHeat, onClick: (e) => e.stopPropagation() })), mode === 'due' && (_jsxs("span", { className: "tbc-ctx-row", onPointerDown: stop, children: [quickDueOptions().map((o) => (_jsx("button", { type: "button", className: `tbc-ctx-chip ${(task.due_date || '').slice(0, 10) === o.iso ? 'tbc-ctx-chip-active' : ''}`, onClick: (e) => {
                            stop(e);
                            onUpdate?.(task.id, { due_date: o.iso });
                            setMode(null);
                        }, children: o.label }, o.label))), _jsx("button", { type: "button", className: "tbc-ctx-chip", onClick: (e) => { stop(e); open('due-pick', task.due_date ? String(task.due_date).slice(0, 10) : ''); }, children: "Pick\u2026" }), task.due_date && (_jsx("button", { type: "button", className: "tbc-ctx-chip", onClick: (e) => { stop(e); onUpdate?.(task.id, { due_date: null }); setMode(null); }, children: "clear" }))] })), showSteps && (childTasks?.length || 0) > 0 && (_jsx("span", { className: "tbc-step-list", onPointerDown: stop, children: childTasks.map((c) => (_jsxs("span", { className: "tbc-step-row", children: [_jsx("button", { type: "button", className: "tbc-step-check", "aria-label": "Complete step", title: "Complete step", onClick: (e) => { stop(e); onCheck?.(c.id); } }), _jsx("span", { className: "tbc-step-title", children: c.title })] }, c.id))) })), clips && (_jsx("span", { className: "tbc-clip-list", onPointerDown: stop, children: clips === 'loading' ? (_jsx("span", { className: "tbc-clip-row tbc-clip-loading", children: "Loading\u2026" })) : clips.length === 0 ? (_jsx("span", { className: "tbc-clip-row tbc-clip-loading", children: "No attachments" })) : (clips.map((c) => {
                    const href = clipHref(c);
                    const stored = !!(c.pointer?.bucket && c.pointer?.path);
                    const isOpen = openClip === c.id;
                    // Synthesized origin (backend, from source_ref): the note /
                    // feed this task came from. Read-only, labeled by source.
                    const isOrigin = c.kind === 'origin';
                    const kindLabel = isOrigin
                        ? (ORIGIN_KIND_LABEL[String(c.pointer?.source || '')] || 'source')
                        : c.kind.replace('_', ' ');
                    return (_jsxs("span", { className: `tbc-clip-row ${isOpen ? 'tbc-clip-row-open' : ''} ${isOrigin ? 'tbc-clip-row-origin' : ''}`, children: [_jsxs("span", { className: "tbc-clip-head", children: [_jsx("span", { className: `tbc-clip-kind ${isOrigin ? 'tbc-clip-kind-origin' : ''}`, children: kindLabel }), href ? (_jsx("a", { className: "tbc-clip-label", href: href, target: "_blank", rel: "noopener noreferrer", onClick: stop, children: clipLabel(c) })) : stored ? (_jsx("button", { type: "button", className: "tbc-clip-label tbc-clip-file", onClick: (e) => { stop(e); openStoredFile(c); }, children: clipLabel(c) })) : (_jsx("button", { type: "button", className: "tbc-clip-label", onClick: (e) => { stop(e); setOpenClip(isOpen ? null : c.id); }, children: clipLabel(c) })), _jsx("button", { type: "button", className: `tbc-clip-chevron ${isOpen ? 'tbc-clip-chevron-open' : ''}`, "aria-label": isOpen ? 'Collapse' : 'Expand', onClick: (e) => { stop(e); setOpenClip(isOpen ? null : c.id); }, children: _jsx(ChevronDown, { size: 10 }) }), onRemoveContext && !isOrigin && (_jsx("button", { type: "button", className: "tbc-clip-x", "aria-label": "Remove attachment", onClick: (e) => {
                                            stop(e);
                                            Promise.resolve(onRemoveContext(task.id, c.id)).then(() => setClips((cur) => (Array.isArray(cur) ? cur.filter((x) => x.id !== c.id) : cur)));
                                        }, children: _jsx(X, { size: 9 }) }))] }), isOpen && (_jsxs("span", { className: "tbc-clip-full", children: [clipFull(c).map((line, i) => /^https?:\/\//i.test(line) ? (_jsx("a", { href: line, target: "_blank", rel: "noopener noreferrer", onClick: stop, children: line }, i)) : (_jsx("span", { children: line }, i))), stored && (_jsx("button", { type: "button", className: "tbc-ctx-chip", onClick: (e) => { stop(e); openStoredFile(c); }, children: "Open file" }))] }))] }, c.id));
                })) })), pendingWho && (_jsxs("span", { className: "tbc-ctx-row tbc-who-relation", onPointerDown: stop, children: [_jsx("span", { className: "tbc-new-name", children: pendingWho.name }), _jsx("button", { type: "button", className: "tbc-ctx-chip", title: "Delegated \u2014 you're waiting on them (nudges apply)", onClick: (e) => { stop(e); commitWho('waiting'); }, children: "Waiting on" }), _jsx("button", { type: "button", className: "tbc-ctx-chip", title: "Raise when you're with them \u2014 1:1 / meeting agenda", onClick: (e) => { stop(e); commitWho('agenda'); }, children: "Agenda" }), _jsx("button", { type: "button", className: "tbc-ctx-chip", title: "Your own action involving them (email / Slack / call)", onClick: (e) => { stop(e); commitWho('involves'); }, children: "Involves" }), _jsx("button", { type: "button", className: "tbc-ctx-chip", onClick: (e) => { stop(e); setPendingWho(null); }, children: "cancel" })] })), mode === 'who' && !pendingWho && (people || []).length > 0 && (() => {
                const q = draft.trim().toLowerCase();
                const unfiltered = !q || q === (task.waiting_for_who || '').toLowerCase() || q === (person || '').toLowerCase();
                const matches = unfiltered
                    ? (people || [])
                    : (people || []).filter((p) => p.name.toLowerCase().includes(q));
                const shown = unfiltered
                    ? [
                        ...matches.filter((p) => p.kind !== 'contact').slice(0, 6),
                        ...matches.filter((p) => p.kind === 'contact').slice(0, 8),
                    ]
                    : matches.slice(0, 14);
                return (_jsxs("span", { className: "tbc-ctx-row", onPointerDown: stop, children: [shown
                            .map((p) => (_jsxs("button", { type: "button", className: `tbc-ctx-chip ${p.name === task.waiting_for_who ? 'tbc-ctx-chip-active' : ''}`, title: [p.email, p.phone].filter(Boolean).join(' · ') || undefined, onClick: (e) => {
                                stop(e);
                                setPendingWho({ name: p.name, email: p.email });
                            }, children: [p.name, p.hint && _jsxs("span", { className: "tbc-chip-hint", children: [" \u00B7 ", p.hint] })] }, `${p.name}:${p.email || ''}`))), onCreatePerson && draft.trim().length > 1 &&
                            !(people || []).some((p) => p.name.toLowerCase() === draft.trim().toLowerCase()) && (_jsxs("button", { type: "button", className: "tbc-ctx-chip tbc-chip-new", onClick: (e) => { stop(e); setNewPerson({ name: draft.trim(), email: '', phone: '' }); }, children: ["+ Add \u201C", draft.trim(), "\u201D"] })), task.waiting_for_who && (_jsx("button", { type: "button", className: "tbc-ctx-chip", onClick: (e) => { stop(e); onUpdate?.(task.id, { waiting_for_who: null }); setMode(null); }, children: "clear" }))] }));
            })(), newPerson && (_jsxs("span", { className: "tbc-ctx-row tbc-new-person", onPointerDown: stop, children: [_jsx("span", { className: "tbc-new-name", children: newPerson.name }), _jsx("input", { className: "tbc-inline-input tbc-new-input", placeholder: "email (optional)", type: "email", value: newPerson.email, autoFocus: true, onChange: (e) => setNewPerson({ ...newPerson, email: e.target.value }), onKeyDown: (e) => { if (e.key === 'Enter') {
                            e.preventDefault();
                            createAndAssign();
                        } if (e.key === 'Escape')
                            setNewPerson(null); }, onClick: stop }), _jsx("input", { className: "tbc-inline-input tbc-new-input", placeholder: "cell (optional)", type: "tel", value: newPerson.phone, onChange: (e) => setNewPerson({ ...newPerson, phone: e.target.value }), onKeyDown: (e) => { if (e.key === 'Enter') {
                            e.preventDefault();
                            createAndAssign();
                        } if (e.key === 'Escape')
                            setNewPerson(null); }, onClick: stop }), _jsx("button", { type: "button", className: "tbc-ctx-chip tbc-ctx-chip-active", onClick: (e) => { stop(e); createAndAssign(); }, children: "Add contact" }), _jsx("button", { type: "button", className: "tbc-ctx-chip", onClick: (e) => { stop(e); setNewPerson(null); }, children: "cancel" })] })), mode === 'acct' && (accounts || []).length > 0 && (() => {
                const q = draft.trim().toUpperCase();
                const unfiltered = !q || q === (task.account_label || '').toUpperCase();
                const shown = (unfiltered
                    ? (accounts || [])
                    : (accounts || []).filter((a) => a.toUpperCase().includes(q))).slice(0, 12);
                return (_jsxs("span", { className: "tbc-ctx-row", onPointerDown: stop, children: [shown
                            .map((a) => (_jsx("button", { type: "button", className: `tbc-ctx-chip ${a.toUpperCase() === (task.account_label || '').toUpperCase() ? 'tbc-ctx-chip-active' : ''}`, onClick: (e) => {
                                stop(e);
                                onUpdate?.(task.id, { account: a });
                                setMode(null);
                            }, children: a }, a))), task.account_label && (_jsx("button", { type: "button", className: "tbc-ctx-chip", onClick: (e) => { stop(e); onUpdate?.(task.id, { account: '' }); setMode(null); }, children: "none" }))] }));
            })(), mode === 'context' && (_jsx("span", { className: "tbc-ctx-row", onPointerDown: stop, children: [null, ...Object.keys(CTX_SLUG)].map((c) => (_jsx("button", { type: "button", className: `tbc-ctx-chip ${((task.context || null) === c) ? 'tbc-ctx-chip-active' : ''}`, onClick: (e) => {
                        stop(e);
                        onUpdate?.(task.id, { context: c });
                        setMode(null);
                    }, children: c || 'none' }, String(c)))) }))] }));
}
// BubbleUpdates speaks the API vocabulary; the overlay speaks the display
// row's ('account' persists an acronym, account_label is what renders).
function toDisplayFields(updates) {
    const fields = {};
    for (const k of Object.keys(updates)) {
        const v = updates[k];
        if (k === 'account')
            fields.account_label = String(v || '').toUpperCase() || null;
        else
            fields[k] = v;
    }
    return fields;
}
export function useUndoableTasks(tasks, onUpdate, onComplete, onReopen) {
    const stackRef = React.useRef([]);
    const [canUndo, setCanUndo] = useState(false);
    const [overlay, setOverlay] = useState({});
    // Server state advanced for a row → its overlay entry has served its
    // purpose (or was wrong); either way the fresh row wins.
    React.useEffect(() => {
        setOverlay((o) => {
            const keys = Object.keys(o);
            if (!keys.length)
                return o;
            let changed = false;
            const next = {};
            for (const id of keys) {
                const t = tasks.find((x) => x.id === id);
                if (t && (t.updated_at ?? null) === o[id].base)
                    next[id] = o[id];
                else
                    changed = true; // row gone or updated_at moved → drop
            }
            return changed ? next : o;
        });
    }, [tasks]);
    const applyOverlay = useCallback((id, updates, base) => {
        setOverlay((o) => ({
            ...o,
            [id]: {
                fields: { ...(o[id]?.fields || {}), ...toDisplayFields(updates) },
                base: o[id]?.base ?? base,
            },
        }));
    }, []);
    const dropOverlay = useCallback((id) => {
        setOverlay((o) => {
            if (!(id in o))
                return o;
            const next = { ...o };
            delete next[id];
            return next;
        });
    }, []);
    const record = (entry) => {
        stackRef.current.push(entry);
        if (stackRef.current.length > 25)
            stackRef.current.shift();
        setCanUndo(true);
    };
    const update = useCallback((id, updates) => {
        const t = tasks.find((x) => x.id === id);
        if (t) {
            const prev = {};
            for (const k of Object.keys(updates)) {
                if (k === 'account')
                    prev.account = t.account_label || '';
                else
                    prev[k] = t[k] ?? null;
            }
            record({ type: 'update', id, prev });
            applyOverlay(id, updates, t.updated_at ?? null);
        }
        const res = onUpdate?.(id, updates);
        // A rejected persist drops the overlay so server truth shows through
        // (the host's own resync/revert handles the rest).
        Promise.resolve(res).catch(() => dropOverlay(id));
        return res;
    }, [tasks, onUpdate, applyOverlay, dropOverlay] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const complete = useCallback((id) => {
        record({ type: 'complete', id });
        return onComplete?.(id);
    }, [onComplete] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const undo = useCallback(() => {
        const e = stackRef.current.pop();
        setCanUndo(stackRef.current.length > 0);
        if (!e)
            return;
        if (e.type === 'update') {
            const t = tasks.find((x) => x.id === e.id);
            if (t)
                applyOverlay(e.id, e.prev, t.updated_at ?? null);
            const res = onUpdate?.(e.id, e.prev);
            Promise.resolve(res).catch(() => dropOverlay(e.id));
        }
        else
            onReopen?.(e.id);
    }, [tasks, onUpdate, onReopen, applyOverlay, dropOverlay]);
    const patched = useMemo(() => (Object.keys(overlay).length
        ? tasks.map((t) => (overlay[t.id] ? { ...t, ...overlay[t.id].fields } : t))
        : tasks), [tasks, overlay]);
    React.useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
                const el = document.activeElement;
                if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'))
                    return; // native text undo wins
                e.preventDefault();
                undo();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [undo]);
    return { tasks: patched, update, complete, undo, canUndo };
}
// ── useOptimisticShelf: archiving must FEEL instantaneous ────────────────
// (Ryan, 2026-08-07): the card leaves the board the moment Archive is
// clicked — the request runs behind it, and a rejection puts the card
// back. ONE mechanism for single, bulk, and unarchive across canvas /
// week / matrix. Entries self-heal against server state (INT-3 contract):
// a hide clears once the parent delivers the row with a DIFFERENT
// archived_at (our write round-tripped) or without the row at all, so a
// fresh fetch always wins and an unarchived row can never stay hidden.
export function useOptimisticShelf(tasks, handlers) {
    // id → archived_at AT CLICK TIME ("was"): the row stays hidden while the
    // parent's data still matches what was clicked on.
    const [hidden, setHidden] = useState({});
    React.useEffect(() => {
        setHidden((h) => {
            const keys = Object.keys(h);
            if (!keys.length)
                return h;
            let changed = false;
            const next = {};
            for (const id of keys) {
                const t = tasks.find((x) => x.id === id);
                if (t && (t.archived_at ?? null) === h[id])
                    next[id] = h[id];
                else
                    changed = true; // row gone or archive state flipped → server caught up
            }
            return changed ? next : h;
        });
    }, [tasks]);
    const mark = (ids) => setHidden((h) => {
        const next = { ...h };
        for (const id of ids)
            next[id] = tasks.find((t) => t.id === id)?.archived_at ?? null;
        return next;
    });
    const revert = (ids) => setHidden((h) => {
        const next = { ...h };
        for (const id of ids)
            delete next[id];
        return next;
    });
    const single = (fn) => fn
        ? (id) => {
            mark([id]);
            Promise.resolve(fn(id)).catch(() => revert([id]));
        }
        : undefined;
    return {
        /** Rows hidden optimistically — every view filters these out. */
        hidden,
        archive: single(handlers.onArchive),
        unarchive: single(handlers.onUnarchive),
        /** Whole selection at once: drop every card NOW, one request behind,
         *  ALL cards return if the batch rejects. */
        archiveMany: handlers.onBulkArchive
            ? (ids) => {
                mark(ids);
                Promise.resolve(handlers.onBulkArchive(ids)).catch(() => revert(ids));
            }
            : undefined,
    };
}
// ── BubbleFocus: the clicked card grows into the FULL editor, centered
//    over a dimmed backdrop. Auto-saves as always; X / backdrop / Esc
//    close; the undo button (and Cmd+Z) reverse mistakes. ─────────────
/** Proposal → chips for the focus editor's review row (was TriageReview). */
function triageChips(task) {
    const p = task.ai_triage?.proposed;
    if (!p)
        return [];
    const chips = [];
    if (p.gtd_list && p.gtd_list !== 'inbox')
        chips.push(p.gtd_list.replace('_', ' '));
    if (p.context)
        chips.push(p.context);
    if (p.priority && p.priority !== 'medium')
        chips.push(p.priority);
    if (p.quick_win)
        chips.push('2-min');
    if (p.agenda_person)
        chips.push(`agenda: ${p.agenda_person}`);
    if (p.waiting_for_who)
        chips.push(`waiting: ${p.waiting_for_who}`);
    if (p.due)
        chips.push(`due ${p.due}`);
    return chips;
}
export function BubbleFocus({ task, onClose, onUndo, canUndo, anchor, onReframe, onAcceptTriage, ...bubbleProps }) {
    const [closing, setClosing] = React.useState(false);
    // Delete lives HERE (hover-cluster rework 2026-08-07): armed two-click,
    // the one place a task can be destroyed outside the bulk bar.
    const [delArmed, setDelArmed] = React.useState(false);
    // Sharpen guard: if the editor closed (or moved to another task) while
    // the reframe round-trip was in flight, drop the response — never apply
    // a title to a task the user is no longer looking at.
    const aliveForRef = React.useRef(task.id);
    React.useEffect(() => {
        aliveForRef.current = task.id;
        return () => { aliveForRef.current = ''; };
    }, [task.id]);
    const requestClose = React.useCallback(() => {
        setClosing(true);
        window.setTimeout(onClose, 160);
    }, [onClose]);
    React.useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape')
                requestClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [requestClose]);
    // Grow-from-the-tile geometry. The editor opens AT the clicked tile;
    // a layout-effect then measures its REAL height and nudges it up only
    // as far as needed to stay on screen (the old fixed `vh - 420` clamp
    // yanked the editor to the top on lower-half cards / short viewports —
    // the "doesn't grow from the card" regression).
    const frameRef = React.useRef(null);
    const [fitTop, setFitTop] = React.useState(null);
    const geom = React.useMemo(() => {
        if (!anchor)
            return null;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const w = Math.min(600, vw - 48); // must match .tbc-focus-frame width
        const left = Math.min(Math.max(16, anchor.left + anchor.width / 2 - w / 2), Math.max(16, vw - w - 16));
        // Clamp only to the viewport itself pre-measure; the layout effect
        // below does the height-aware fit before first paint.
        const top = Math.min(Math.max(16, anchor.top - 12), Math.max(16, vh - 120));
        return {
            left,
            top,
            scale: Math.max(0.4, Math.min(0.92, anchor.width / w)),
        };
    }, [anchor]);
    React.useLayoutEffect(() => {
        setFitTop(null);
        if (!geom || !frameRef.current)
            return;
        const vh = window.innerHeight;
        const h = frameRef.current.offsetHeight;
        const fitted = Math.max(16, Math.min(geom.top, vh - h - 16));
        if (Math.abs(fitted - geom.top) > 1)
            setFitTop(fitted);
    }, [geom]);
    const top = fitTop ?? geom?.top ?? 0;
    return (_jsx("div", { className: ['tbc-focus-overlay', geom ? 'tbc-focus-anchored' : '', closing ? 'tbc-focus-closing' : ''].filter(Boolean).join(' '), onMouseDown: (e) => { if (e.target === e.currentTarget)
            requestClose(); }, children: _jsxs("div", { ref: frameRef, className: "tbc-focus-frame", style: geom && anchor ? {
                position: 'fixed',
                left: geom.left,
                top,
                // Origin tracks the TILE even after the fit-nudge, so the grow/
                // shrink animation always comes from (returns to) the card.
                transformOrigin: (anchor.left + anchor.width / 2 - geom.left) + 'px ' +
                    (anchor.top + anchor.height / 2 - top) + 'px',
                '--tbc-grow-scale': String(geom.scale),
                '--tbc-focus-maxh': Math.max(200, window.innerHeight - top - 24) + 'px',
            } : undefined, children: [_jsxs("span", { className: "tbc-focus-acts", children: [onReframe && (_jsx("button", { type: "button", className: "tbc-act", title: "Sharpen \u2014 reframe into VERB NOUN: who, what, when", "aria-label": "Sharpen title", onClick: () => {
                                const forId = task.id;
                                void Promise.resolve(onReframe(forId)).then((title) => {
                                    if (title && aliveForRef.current === forId)
                                        bubbleProps.onUpdate?.(forId, { title });
                                });
                            }, children: _jsx(Wand2, { size: 12 }) })), onUndo && (_jsx("button", { type: "button", className: "tbc-act", title: "Undo last change (Cmd+Z)", "aria-label": "Undo", disabled: !canUndo, onClick: onUndo, children: _jsx(Undo2, { size: 12 }) })), task.archived_at && bubbleProps.onUnarchive && (_jsx("button", { type: "button", className: "tbc-act", title: "Unarchive \u2014 back to the boards exactly as it was", "aria-label": "Unarchive task", onClick: () => {
                                void Promise.resolve(bubbleProps.onUnarchive?.(task.id));
                                requestClose();
                            }, children: _jsx(ArchiveRestore, { size: 12 }) })), !task.archived_at && bubbleProps.onArchive && (_jsx("button", { type: "button", className: "tbc-act", title: "Archive \u2014 shelve, recover any time under the Archived lens", "aria-label": "Archive task", onClick: () => {
                                void Promise.resolve(bubbleProps.onArchive?.(task.id));
                                requestClose();
                            }, children: _jsx(Archive, { size: 12 }) })), bubbleProps.onDelete && (_jsx("button", { type: "button", className: `tbc-act tbc-act-delete ${delArmed ? 'tbc-act-armed' : ''}`, title: delArmed ? 'Click again to delete' : 'Delete', "aria-label": delArmed ? 'Confirm delete task' : 'Delete task', onClick: () => {
                                if (!delArmed) {
                                    setDelArmed(true);
                                    window.setTimeout(() => setDelArmed(false), 2500);
                                }
                                else {
                                    void Promise.resolve(bubbleProps.onDelete?.(task.id));
                                    requestClose();
                                }
                            }, children: _jsx(Trash2, { size: 12 }) })), _jsx("button", { type: "button", className: "tbc-act", title: "Close (Esc)", "aria-label": "Close", onClick: requestClose, children: _jsx(X, { size: 12 }) })] }), (task.triage_state === 'needs_review' || task.triage_state === 'auto_triaged') &&
                    (onAcceptTriage || bubbleProps.onUpdate) && (_jsxs("div", { className: "tbc-triage-row", role: "group", "aria-label": "AI triage review", children: [_jsxs("span", { className: "tbc-triage-note", children: [task.triage_state === 'needs_review'
                                    ? (task.ai_triage?.duplicate_of ? 'AI: possible duplicate' : 'AI: low confidence')
                                    : 'AI-triaged', task.ai_triage?.rationale ? ` — ${task.ai_triage.rationale}` : ''] }), _jsx("span", { className: "tbc-triage-chips", children: triageChips(task).map((c) => (_jsx("span", { className: "tbc-pill tbc-pill-tag", children: c }, c))) }), onAcceptTriage && (_jsxs("button", { type: "button", className: "tbc-ctx-chip tbc-ctx-chip-active", title: task.triage_state === 'needs_review' ? 'Apply the proposal' : 'Confirm the applied triage', onClick: () => void Promise.resolve(onAcceptTriage(task.id)), children: [_jsx(Check, { size: 11 }), " Accept"] })), task.triage_state === 'needs_review' && bubbleProps.onUpdate && (_jsx("button", { type: "button", className: "tbc-ctx-chip", title: "Confirm without applying the proposal", onClick: () => bubbleProps.onUpdate?.(task.id, { triage_state: 'confirmed' }), children: "Keep as is" }))] })), _jsx(Bubble, { task: task, dragDisabled: true, ...bubbleProps }), task.goal_outcome && (_jsxs("div", { className: "tbc-goal-line", title: "This task advances a goal you chose", children: ["advances: ", task.goal_outcome] })), bubbleProps.onUpdate && (_jsxs("div", { className: "tbc-origin-row", role: "group", "aria-label": "Work origin", children: [_jsx("span", { className: "tbc-origin-label", title: "Who initiated this work? Drives the proactive-vs-reactive time ratio.", children: "origin" }), [
                            ['self', 'I chose this — hunting, selling, my own plan'],
                            ['emergent', 'Came out of doing the work — meetings, customers'],
                            ['assigned', 'Someone directed it at me'],
                            ['maintenance', 'Recurring obligation / admin'],
                        ].map(([value, hint]) => (_jsx("button", { type: "button", className: `tbc-origin-chip ${task.origin === value ? 'tbc-origin-chip-active' : ''}`, title: hint, "aria-pressed": task.origin === value, onClick: () => bubbleProps.onUpdate?.(task.id, {
                                origin: task.origin === value ? null : value,
                            }), children: value === 'maintenance' ? 'maint.' : value }, value)))] })), bubbleProps.onUpdate && (_jsx("textarea", { className: "tbc-focus-notes", placeholder: "Notes\u2026", defaultValue: task.description || '', "aria-label": "Task notes", onBlur: (e) => {
                        const v = e.target.value.trim();
                        if (v !== (task.description || ''))
                            bubbleProps.onUpdate?.(task.id, { description: v || null });
                    } }))] }) }));
}
// ── TaskFilterBar: ONE bar shared by every view ──────────────────────────
export function TaskFilterBar({ tasks, accounts, value, onChange, canUndo, onUndo, onAcceptAll, onCreate, createHint, }) {
    const [acctOpen, setAcctOpen] = useState(false);
    const [lensMenu, setLensMenu] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [createTitle, setCreateTitle] = useState('');
    const [createAcct, setCreateAcct] = useState('');
    const [createdFlash, setCreatedFlash] = useState(false);
    const submitCreate = () => {
        const t = createTitle.trim();
        if (!t || !onCreate)
            return;
        void Promise.resolve(onCreate({ title: t, account: createAcct || null }));
        setCreateTitle('');
        setCreatedFlash(true);
        window.setTimeout(() => setCreatedFlash(false), 1200);
    };
    const lenses = useMemo(() => deriveLenses(tasks), [tasks]);
    const lens = lenses.find((l) => l.key === value.lensKey) || lenses[0];
    const vt = (fn) => {
        const doc = document;
        if (typeof doc.startViewTransition === 'function')
            doc.startViewTransition(fn);
        else
            fn();
    };
    const set = (patch) => onChange({ ...value, ...patch });
    const chipLenses = lenses.filter((l) => ['focus', 'all', 'quick', 'waiting', 'review', 'done'].includes(l.key));
    const groups = [
        { key: 'time', label: 'Timeframe', items: lenses.filter((l) => ['week', 'stale', 'archived'].includes(l.key)) },
        { key: 'ctx', label: 'Context', items: lenses.filter((l) => l.key.startsWith('ctx:')) },
        { key: 'who', label: 'People', items: lenses.filter((l) => l.key.startsWith('who:')) },
    ];
    const acctOptions = accounts && accounts.length
        ? accounts
        : [...new Set(tasks.map((t) => t.account_label).filter(Boolean))];
    return (_jsxs("div", { className: "tbc-canvas tbc-filter-host", children: [_jsxs("div", { className: "tbc-lens-bar", role: "tablist", "aria-label": "Lenses", children: [_jsxs("span", { className: "tbc-search-wrap", children: [_jsx(Search, { size: 13, "aria-hidden": true }), _jsx("input", { className: "tbc-search", type: "search", placeholder: "Search tasks\u2026", value: value.query, onChange: (e) => set({ query: e.target.value }), "aria-label": "Search tasks" })] }), chipLenses.map((l) => {
                        const n = l.key === 'review' ? tasks.filter(l.test).length : 0;
                        return (_jsxs("button", { type: "button", role: "tab", "aria-selected": l.key === lens.key, className: `tbc-lens ${l.key === lens.key ? 'tbc-lens-active' : ''}`, onClick: () => vt(() => set({ lensKey: l.key })), children: [l.label, n > 0 ? ` · ${n}` : ''] }, l.key));
                    }), lens.key === 'review' && onAcceptAll && (_jsxs("button", { type: "button", className: "tbc-lens tbc-lens-accept-all", title: "Accept every AI proposal in this list", onClick: () => {
                            const ids = tasks.filter(lens.test).map((t) => t.id);
                            if (ids.length)
                                void Promise.resolve(onAcceptAll(ids));
                        }, children: [_jsx(CheckCheck, { size: 13 }), " Accept all"] })), groups.filter((g) => g.items.length > 0).map((g) => {
                        const activeIn = g.items.find((l) => l.key === lens.key);
                        return (_jsxs("span", { className: "tbc-lens-group", children: [_jsxs("button", { type: "button", className: `tbc-lens ${activeIn || lensMenu === g.key ? 'tbc-lens-active' : ''}`, title: `Filter by ${g.label.toLowerCase()}`, onClick: () => setLensMenu(lensMenu === g.key ? null : g.key), children: [activeIn ? activeIn.label : g.label, _jsx(ChevronDown, { size: 10 })] }), lensMenu === g.key && (_jsxs(_Fragment, { children: [_jsx("span", { className: "tbc-lens-veil", onMouseDown: () => setLensMenu(null) }), _jsxs("span", { className: "tbc-lens-menu", role: "menu", children: [activeIn && (_jsx("button", { type: "button", onClick: () => { vt(() => set({ lensKey: 'focus' })); setLensMenu(null); }, children: "Clear \u2014 back to Focus" })), g.items.map((l) => (_jsx("button", { type: "button", className: l.key === lens.key ? 'tbc-lopt-on' : '', onClick: () => { vt(() => set({ lensKey: l.key })); setLensMenu(null); }, children: l.label }, l.key)))] })] }))] }, g.key));
                    }), _jsxs("button", { type: "button", className: `tbc-lens ${value.acctFilter || acctOpen ? 'tbc-lens-active' : ''}`, title: "Filter to one of your accounts", onClick: () => setAcctOpen(!acctOpen), children: [value.acctFilter || 'Accounts', _jsx(ChevronDown, { size: 10 })] }), canUndo && onUndo && (_jsx("button", { type: "button", className: "tbc-lens", title: "Undo last change (Cmd+Z)", "aria-label": "Undo", onClick: onUndo, children: _jsx(Undo2, { size: 13 }) })), onCreate && (_jsxs("button", { type: "button", className: `tbc-lens tbc-newtask ${createOpen ? 'tbc-newtask-open' : ''}`, title: "New task (Enter saves to the inbox)", "aria-expanded": createOpen, onClick: () => setCreateOpen(!createOpen), children: [_jsx(Plus, { size: 13 }), " New task"] }))] }), onCreate && createOpen && (_jsxs("div", { className: "tbc-create-row", role: "group", "aria-label": "New task", children: [_jsx("input", { className: "tbc-create-input", placeholder: createHint || 'What needs doing? Enter saves — first line is the title', value: createTitle, autoFocus: true, onChange: (e) => setCreateTitle(e.target.value), onKeyDown: (e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                submitCreate();
                            }
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                setCreateOpen(false);
                                setCreateTitle('');
                            }
                        }, "aria-label": "New task title" }), (acctOptions || []).length > 0 && (_jsxs("select", { className: "tbc-create-acct", value: createAcct, onChange: (e) => setCreateAcct(e.target.value), "aria-label": "Account (optional)", title: "Link to an account (optional)", children: [_jsx("option", { value: "", children: "No account" }), acctOptions.map((a) => (_jsx("option", { value: a, children: a }, a)))] })), createdFlash && _jsxs("span", { className: "tbc-create-flash", role: "status", children: [_jsx(Check, { size: 12 }), " added"] }), _jsx("button", { type: "button", className: "tbc-act tbc-create-close", title: "Close (Esc)", "aria-label": "Close quick create", onClick: () => { setCreateOpen(false); setCreateTitle(''); }, children: _jsx(X, { size: 11 }) })] })), acctOpen && (_jsxs("div", { className: "tbc-acct-row", role: "listbox", "aria-label": "Filter by account", children: [_jsx("button", { type: "button", className: `tbc-ctx-chip ${!value.acctFilter ? 'tbc-ctx-chip-active' : ''}`, onClick: () => vt(() => { set({ acctFilter: null }); setAcctOpen(false); }), children: "All accounts" }), acctOptions.map((a) => (_jsx("button", { type: "button", className: `tbc-ctx-chip ${value.acctFilter === a ? 'tbc-ctx-chip-active' : ''}`, onClick: () => vt(() => { set({ acctFilter: a }); setAcctOpen(false); }), children: a }, a))), _jsx("button", { type: "button", className: `tbc-ctx-chip ${value.clusterOn ? 'tbc-ctx-chip-active' : ''}`, title: "Group busy zones under account headers", onClick: () => vt(() => set({ clusterOn: !value.clusterOn })), children: "Group by account" })] }))] }));
}
// ── Zone ──────────────────────────────────────────────────────────────────
function Zone({ zone, shownCount, total, hidden, expanded, onToggleExpand, quickAdd, children, }) {
    const { setNodeRef, isOver } = useDroppable({ id: `zone:${zone.key}` });
    const filtered = shownCount + hidden;
    return (_jsxs("section", { ref: setNodeRef, className: `tbc-zone tbc-zone-${zone.key} ${isOver ? 'tbc-zone-over' : ''}`, "aria-label": zone.label, children: [_jsxs("header", { className: "tbc-zone-head", title: zone.hint, children: [_jsx("span", { className: "tbc-zone-label", children: zone.label }), _jsx("span", { className: "tbc-zone-count", title: filtered < total ? `${filtered} shown by this lens · ${total} total` : `${total} task(s)`, children: filtered < total ? `${filtered} / ${total}` : total })] }), _jsxs("div", { className: "tbc-zone-body", children: [children, shownCount === 0 && hidden === 0 && _jsx("span", { className: "tbc-zone-empty", children: zone.hint }), (hidden > 0 || expanded) && (_jsx("button", { type: "button", className: "tbc-more", onClick: onToggleExpand, children: expanded ? 'Show less' : `+${hidden} more` })), quickAdd] })] }));
}
// ── Canvas ────────────────────────────────────────────────────────────────
export function TaskBubbleCanvas({ tasks: tasksProp, onMove, onComplete, onReopen, onGroup, onUpdate, onAttach, onAttachFile, onGetContextUrl, onLoadContext, onRemoveContext, onDelete, onCreate, onArchive, onUnarchive, onBulkArchive, onRenumber, onSelect, accounts, people, onAssign, onCreatePerson, renderBadge, filter: filterProp, onFilterChange, hideFilterBar, onLogEvents, onReframe, onAcceptTriage, zoneCap = 60, }) {
    // Keyboard path (UX P2-8): bubbles are focusable; Space/Enter lifts,
    // arrows move, Space/Enter drops — same collision rules as the pointer.
    // Touch splits from mouse (phone pass 2026-08-12): a distance-only
    // PointerSensor turned every scroll flick on a card into a drag. Mouse
    // keeps drag-on-6px; touch requires a long-press to lift, so flicks
    // scroll (bubbles are touch-action: pan-y) and taps select.
    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }), useSensor(KeyboardSensor));
    const [filterInternal, setFilterInternal] = useState(DEFAULT_TASK_FILTER);
    const filter = filterProp ?? filterInternal;
    const setFilter = onFilterChange ?? setFilterInternal;
    const { lensKey, query, acctFilter, clusterOn } = filter;
    const [expandedZones, setExpandedZones] = useState({});
    const [selected, setSelected] = useState({});
    const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
    const toggleSelect = useCallback((id) => {
        setSelected((s2) => ({ ...s2, [id]: !s2[id] }));
    }, []);
    const clearSelection = useCallback(() => {
        setSelected({});
    }, []);
    React.useEffect(() => {
        if (!selectedIds.length)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                clearSelection();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedIds.length, clearSelection]);
    // Undo stack + the optimistic field overlay: `tasks` from here on is the
    // prop with in-flight edits applied, so every derived memo below paints
    // the user's change instantly (UI-responsiveness review 2026-08-08).
    const { tasks, update: undoableUpdate, complete: undoableComplete, undo, canUndo } = useUndoableTasks(tasksProp, onUpdate, onComplete, onReopen);
    const [activeId, setActiveId] = useState(null);
    const [overBubbleId, setOverBubbleId] = useState(null);
    const [insertAt, setInsertAt] = useState(null);
    // Optimistic state: zone/position overrides (revert on reject) + locally
    // completed/grouped bubbles (hidden until the parent's data catches up).
    // INT-3 (audit): each override remembers the task's updated_at at the
    // moment of the move; once the parent delivers a row with a DIFFERENT
    // updated_at (our write round-tripped, or someone else's), the override
    // clears so fresh server state always wins.
    const [overrides, setOverrides] = useState({});
    const [completing, setCompleting] = useState({});
    const [gone, setGone] = useState({});
    // Archive/unarchive leave the board the instant they're clicked; the
    // request runs behind, a rejection restores the card.
    const shelf = useOptimisticShelf(tasks, { onArchive, onUnarchive, onBulkArchive });
    React.useEffect(() => {
        setOverrides((o) => {
            const keys = Object.keys(o);
            if (!keys.length)
                return o;
            let changed = false;
            const next = {};
            for (const id of keys) {
                const t = tasks.find((x) => x.id === id);
                if (t && (t.updated_at ?? null) === o[id].base)
                    next[id] = o[id];
                else
                    changed = true; // task gone or server state advanced → drop
            }
            return changed ? next : o;
        });
    }, [tasks]);
    // Smooth morphs between zones/lenses where the browser supports it.
    const vt = (fn) => {
        const doc = document;
        if (!dense && typeof doc.startViewTransition === 'function')
            doc.startViewTransition(fn);
        else
            fn();
    };
    const [focused, setFocused] = useState(null);
    const lenses = useMemo(() => deriveLenses(tasks), [tasks]);
    const lens = lenses.find((l) => l.key === lensKey) || lenses[0];
    const active = useMemo(() => tasks
        .filter((t) => !t.status || t.status !== 'cancelled')
        .filter((t) => !gone[t.id] && !(t.id in shelf.hidden))
        .map((t) => (overrides[t.id] ? { ...t, ...overrides[t.id].fields } : t)), [tasks, overrides, gone, shelf.hidden]);
    // Project children live INSIDE their parent bubble, not loose on the canvas.
    const childrenByParent = useMemo(() => {
        const map = {};
        for (const t of active) {
            if (t.project_id)
                (map[t.project_id] = map[t.project_id] || []).push(t);
        }
        return map;
    }, [active]);
    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return active
            .filter((t) => !t.project_id)
            .filter((t) => !acctFilter || (t.account_label || '').toUpperCase() === acctFilter.toUpperCase())
            .filter((t) => lens.test(t))
            .filter((t) => !q ||
            t.title.toLowerCase().includes(q) ||
            (t.account_label || '').toLowerCase().includes(q) ||
            (t.waiting_for_who || '').toLowerCase().includes(q) ||
            (t.tags || []).some((tag) => tag.toLowerCase().includes(q)));
    }, [active, lens, query, acctFilter]);
    // Density guard: past ~90 visible bubbles, drop entrance animation and
    // view-transition morphs — smoothness over spectacle.
    const dense = visible.length > 90;
    const totalsByZone = useMemo(() => {
        const totals = {};
        for (const t of active) {
            if (t.project_id)
                continue;
            totals[t.gtd_list] = (totals[t.gtd_list] || 0) + 1;
        }
        return totals;
    }, [active]);
    const byZone = useMemo(() => {
        const grouped = {};
        for (const z of ZONES)
            grouped[z.key] = [];
        for (const t of visible) {
            (grouped[t.gtd_list] || grouped.inbox).push(t);
        }
        for (const key of Object.keys(grouped)) {
            grouped[key].sort((a, b) => (a.position || 0) - (b.position || 0));
        }
        return grouped;
    }, [visible]);
    const activeTask = activeId ? tasks.find((t) => t.id === activeId) : null;
    const handleDelete = onDelete
        ? (id) => {
            setGone((g) => ({ ...g, [id]: true }));
            Promise.resolve(onDelete(id)).catch(() => {
                setGone((g) => {
                    const next = { ...g };
                    delete next[id];
                    return next;
                });
            });
        }
        : undefined;
    const handleCheck = useCallback((id) => {
        setCompleting((c) => ({ ...c, [id]: true }));
        window.setTimeout(() => {
            setGone((g) => ({ ...g, [id]: true }));
            setCompleting((c) => {
                const next = { ...c };
                delete next[id];
                return next;
            });
            Promise.resolve(undoableComplete?.(id)).catch(() => {
                setGone((g) => {
                    const next = { ...g };
                    delete next[id];
                    return next;
                });
            });
        }, 380);
    }, [onComplete]);
    const handleDragStart = (e) => {
        setActiveId(String(e.active.id).replace('bubble:', ''));
    };
    /**
     * Drop intent on a bubble: the CENTER band groups into a project; the
     * left/right edges insert before/after it (reorder). Uses the pointer's
     * final x within the target rect.
     */
    const dropIntent = (e, overRect) => {
        const a = e.activatorEvent;
        if (!a || !overRect || !overRect.width)
            return 'group';
        const x = (a.clientX || 0) + e.delta.x;
        const rel = (x - overRect.left) / overRect.width;
        // Wider edge bands: reordering is the common intent, grouping the rare one.
        if (rel < 0.35)
            return 'before';
        if (rel > 0.65)
            return 'after';
        return 'group';
    };
    const handleDragOver = (e) => {
        const overId = e.over ? String(e.over.id) : '';
        const isBubble = overId.startsWith('onto:');
        if (!isBubble) {
            setOverBubbleId(null);
            setInsertAt(null);
            return;
        }
        const intent = dropIntent(e, e.over?.rect);
        if (intent === 'group' && onGroup) {
            setOverBubbleId(overId.slice(5));
            setInsertAt(null);
        }
        else if (intent === 'before' || intent === 'after') {
            setOverBubbleId(null);
            setInsertAt({ id: overId.slice(5), side: intent });
        }
        else {
            setOverBubbleId(null);
            setInsertAt(null);
        }
    };
    const applyMove = (id, gtdList, position) => {
        if (!onMove)
            return;
        const base = tasks.find((t) => t.id === id)?.updated_at ?? null;
        vt(() => setOverrides((o) => ({ ...o, [id]: { fields: { gtd_list: gtdList, position }, base } })));
        Promise.resolve(onMove(id, { gtd_list: gtdList, position })).catch(() => {
            setOverrides((o) => {
                const next = { ...o };
                delete next[id];
                return next;
            });
        });
    };
    const handleDragEnd = (e) => {
        const id = String(e.active.id).replace('bubble:', '');
        setActiveId(null);
        setOverBubbleId(null);
        setInsertAt(null);
        if (!e.over)
            return;
        const overId = String(e.over.id);
        // Dropped onto another bubble: center = group, edges = insert.
        if (overId.startsWith('onto:')) {
            const targetId = overId.slice(5);
            if (targetId === id)
                return;
            const target = visible.find((t) => t.id === targetId);
            if (!target)
                return;
            const intent = dropIntent(e, e.over.rect);
            if (intent === 'group' && onGroup) {
                setGone((g) => ({ ...g, [id]: true }));
                Promise.resolve(onGroup(id, targetId)).catch(() => {
                    setGone((g) => {
                        const next = { ...g };
                        delete next[id];
                        return next;
                    });
                });
                return;
            }
            // Reorder: insert before/after the target in ITS zone (works across
            // zones too — precise placement while retriaging).
            const zoneTasks = (byZone[target.gtd_list] || []).filter((t) => t.id !== id);
            const idx = zoneTasks.findIndex((t) => t.id === targetId);
            const tPos = target.position || 0;
            let position;
            let neighborPos = null;
            if (intent === 'before') {
                const prev = zoneTasks[idx - 1];
                neighborPos = prev ? (prev.position || 0) : null;
                position = prev ? ((prev.position || 0) + tPos) / 2 : tPos - 512;
            }
            else {
                const next = zoneTasks[idx + 1];
                neighborPos = next ? (next.position || 0) : null;
                position = next ? (tPos + (next.position || 0)) / 2 : tPos + 512;
            }
            // INT-2 (audit): bisection degenerates when neighbors tie (fresh
            // boards full of default positions) or float precision exhausts —
            // the midpoint lands ON a neighbor and the drop visibly no-ops.
            // Renumber the zone to a clean ladder, then place off the fresh map.
            if (onRenumber && (position === tPos || (neighborPos != null && position === neighborPos))) {
                void Promise.resolve(onRenumber(target.gtd_list)).then((map) => {
                    const targetPos = map?.[targetId];
                    if (typeof targetPos !== 'number')
                        return;
                    applyMove(id, target.gtd_list, intent === 'before' ? targetPos - 512 : targetPos + 512);
                });
                return;
            }
            applyMove(id, target.gtd_list, position);
            return;
        }
        // Dropped on zone background → move to the end of that zone (also how
        // you send a bubble to the back of its own zone).
        if (overId.startsWith('zone:')) {
            const zoneKey = overId.slice(5);
            const task = visible.find((t) => t.id === id);
            if (!task)
                return;
            const zoneTasks = (byZone[zoneKey] || []).filter((t) => t.id !== id);
            const maxPos = zoneTasks.reduce((m, t) => Math.max(m, t.position || 0), 0);
            if (task.gtd_list === zoneKey && (task.position || 0) > maxPos)
                return; // already last
            applyMove(id, zoneKey, maxPos + 512);
        }
    };
    return (_jsxs("div", { className: `tbc-canvas ${selectedIds.length ? 'tbc-selmode' : ''}`, children: [!hideFilterBar && (_jsx(TaskFilterBar, { tasks: tasks, accounts: accounts, value: filter, onChange: setFilter, canUndo: canUndo, onUndo: undo, onCreate: onCreate })), _jsxs(DndContext, { sensors: sensors, collisionDetection: pointerWithin, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragMove: handleDragOver, onDragEnd: handleDragEnd, children: [_jsx("div", { className: "tbc-grid", children: ZONES.map((z) => {
                            const zoneTasks = byZone[z.key] || [];
                            // A narrowing lens (Waiting, a person, a context, Done…) hides
                            // empty zones entirely — only Focus/All keep the full board
                            // structure with hints (Ryan, 2026-08-07).
                            if (!['focus', 'all'].includes(lens.key) && zoneTasks.length === 0)
                                return null;
                            const isExpanded = !!expandedZones[z.key];
                            let shown = zoneTasks;
                            if (!isExpanded && zoneTasks.length > zoneCap) {
                                // Cap keeps the most URGENT, displayed in manual position order.
                                const keep = new Set([...zoneTasks].sort((a, b) => urgency(b) - urgency(a)).slice(0, zoneCap).map((t) => t.id));
                                shown = zoneTasks.filter((t) => keep.has(t.id));
                            }
                            return (_jsx(Zone, { zone: z, shownCount: shown.length, total: totalsByZone[z.key] || 0, hidden: Math.max(0, zoneTasks.length - shown.length), expanded: isExpanded, onToggleExpand: () => setExpandedZones((e) => ({ ...e, [z.key]: !e[z.key] })), quickAdd: onCreate ? (_jsx(TaskQuickAdd, { title: `New task in ${z.label}`, placeholder: `New in ${z.label}…`, onSubmit: (title) => onCreate({ title, gtd_list: z.key }) })) : undefined, children: clusterAccounts(shown, clusterOn).map((group) => (_jsxs(React.Fragment, { children: [group.label !== undefined && (_jsxs("span", { className: `tbc-cluster-head ${group.label ? '' : 'tbc-cluster-head-none'}`, children: [_jsx("span", { className: "tbc-cluster-label", children: group.label || 'General' }), _jsx("span", { className: "tbc-cluster-count", children: group.tasks.length })] })), group.tasks.map((t) => (_jsx(Bubble, { task: t, childTasks: childrenByParent[t.id], dense: dense, compact: true, isSelected: !!selected[t.id], onToggleSelect: toggleSelect, dragging: t.id === activeId, completing: !!completing[t.id], groupTarget: t.id === overBubbleId, insertSide: insertAt && insertAt.id === t.id ? insertAt.side : null, onCheck: handleCheck, onReopen: onReopen, onSelect: (task2, mode2, anchor2) => {
                                                onSelect?.(task2, mode2, anchor2);
                                                setFocused({ id: task2.id, mode: mode2, anchor: anchor2 });
                                            }, onUpdate: undoableUpdate, onAttach: onAttach, onAttachFile: onAttachFile, onGetContextUrl: onGetContextUrl, onLoadContext: onLoadContext, onRemoveContext: onRemoveContext, onDelete: handleDelete, onArchive: shelf.archive, onUnarchive: shelf.unarchive, accounts: accounts, people: people, onAssign: onAssign, onCreatePerson: onCreatePerson, renderBadge: renderBadge }, t.id)))] }, group.label ?? '__flat'))) }, z.key));
                        }) }), _jsx(DragOverlay, { dropAnimation: null, children: activeTask ? _jsx(Bubble, { task: activeTask, overlay: true }) : null })] }), focused && (() => {
                const t = tasks.find((x) => x.id === focused.id);
                if (!t)
                    return null;
                return (_jsx(BubbleFocus, { task: overrides[t.id] ? { ...t, ...overrides[t.id].fields } : t, initialMode: focused.mode, anchor: focused.anchor, onReframe: onReframe, onAcceptTriage: onAcceptTriage, onClose: () => setFocused(null), onUndo: undo, canUndo: canUndo, onCheck: handleCheck, onReopen: onReopen, onUpdate: undoableUpdate, onAttach: onAttach, onAttachFile: onAttachFile, onGetContextUrl: onGetContextUrl, onLoadContext: onLoadContext, onRemoveContext: onRemoveContext, onDelete: handleDelete, onArchive: shelf.archive, onUnarchive: shelf.unarchive, onAssign: onAssign, onCreatePerson: onCreatePerson, accounts: accounts, people: people }));
            })(), _jsx(TaskBulkBar, { tasks: visible.filter((t) => selected[t.id]), people: people, moveOptions: ZONES.map((z) => ({ key: z.key, label: z.label })), moveLabel: "Move to list", onMove: onMove ? (id, key) => onMove(id, { gtd_list: key, position: Date.now() }) : undefined, onComplete: onComplete ? (id) => onComplete(id) : undefined, onArchive: shelf.archive, onBulkArchive: shelf.archiveMany, onDelete: handleDelete, onAssign: onAssign, onLogEvents: onLogEvents, onClear: clearSelection })] }));
}
export default TaskBubbleCanvas;
